import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { getAgeISO } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'

import { ClientDetailsSideBarSection, ClientDetailsSideBarSectionItem } from './ClientDetailsSideBarSection'
import { CheckIcon } from '../../atoms/icons/CheckIcon'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { Details } from '../../ui-models/messages/Details'
import { tID } from '../../utils/utils'

const VoicemailCheckmarkContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
})

export type ClientDetailsMainSideBarSectionProps = {
  details: Details
}

export const ClientDetailsMainSideBarSection: React.FC<ClientDetailsMainSideBarSectionProps> = ({ details }) => {
  const intl = useIntl()
  const { colors } = useTheme()

  const voicemailToolTipContent = (
    <FormattedMessage
      defaultMessage='{clientFirstName} provided voicemail consent'
      description='Tooltip message indicating that the client has provided voicemail consent'
      values={{ clientFirstName: details.client.firstName || details.client.legalName }}
    />
  )

  const voicemailCheckmark = details.client.canLeaveVoicemail ? (
    <VoicemailCheckmarkContainer testID={tID('Voicemail-checkmark')}>
      <Tooltip
        placement='top'
        content={voicemailToolTipContent}
        accessibilityLabel={intl.formatMessage({
          defaultMessage: 'Voicemail consent indicator',
          description: 'Accessibility label for voicemail consent checkmark icon',
        })}
      >
        <CheckIcon fillColor={colors.iconSuccess} size={24} />
      </Tooltip>
    </VoicemailCheckmarkContainer>
  ) : undefined

  const items: ClientDetailsSideBarSectionItem[] = [
    {
      key: 'legal_name',
      label: <FormattedMessage defaultMessage='Legal name' description='Legal name of client' />,
      textValue: details.client.legalName,
      testID: tID('ClientDetailsMainSideBarSection-legal-name'),
    },
    !!details.client.preferredName?.trim()
      ? {
          key: 'preferred_name',
          label: <FormattedMessage defaultMessage='Preferred name' description='Preferred name of client' />,
          textValue: details.client.preferredName,
          testID: tID('ClientDetailsMainSideBarSection-preferred-name'),
        }
      : null,
    details.client.timeZone
      ? {
          key: 'time_zone',
          label: <FormattedMessage defaultMessage='Time zone' description='Time zone of client' />,
          textValue: details.client.timeZone,
          testID: tID('ClientDetailsMainSideBarSection-time-zone'),
        }
      : null,
    {
      key: 'date_of_birth',
      label: <FormattedMessage defaultMessage='DOB' description='DOB of client' />,
      textValue: (
        <FormattedMessage
          defaultMessage='{dateOfBirth} ({age})'
          description='DOB of client'
          values={{
            age: getAgeISO(details.client.dateOfBirth),
            dateOfBirth: intl.formatDate(details.client.dateOfBirth, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            }),
          }}
        />
      ),
      testID: tID('ClientDetailsMainSideBarSection-date-of-birth'),
    },
    !details.client.hideClientContactInfo && !details.client.hideClientPhone
      ? {
          key: 'phone_number',
          label: <FormattedMessage defaultMessage='Phone' description='Phone number of client' />,
          textValue: details.client.phoneNumber,
          testID: tID('ClientDetailsMainSideBarSection-phone-number'),
          suffix: voicemailCheckmark,
        }
      : null,
  ].filter(Boolean) as ClientDetailsSideBarSectionItem[]
  return (
    <ClientDetailsSideBarSection
      headerText={<FormattedMessage defaultMessage='Client details' description='Header text for client details' />}
      items={items}
    />
  )
}
